// Epic 7 re-skin (ADO-903) — the unified /products catalogue page.
//
// Consolidates the former /browse and /search screens under one URL and one
// two-bar SiteHeader (the customer rejected the separate /browse and /search
// URLs) and lays them out like the designer prototype's category-listing:
//   • Browse (Story 7.1 department selector) is a HEADER MEGA-MENU — the Browse
//     button opens BrowseMenu under the sub-nav (the column mega-menu, or the
//     side tray on mobile, ADO-1779).
//   • the body is the CategoryListing (filters sidebar + product grid) for the
//     selected node, auto-loaded to the first department so the grid is populated
//     on arrival; a committed search swaps it for the ranked search results.
// Browse/search/CategoryListing keep all their tested data logic (drill,
// load-more guard, search abort/seq guard, filters, availability, favourites).
//
// The auth gate lives on the /products route in main.tsx.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { SiteHeader } from '../../design-system';
import { useSessionStore } from '../../auth/useSessionStore';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { isAdminRole } from '../../auth/rbac';
import {
  appendScopeParams,
  productScopeFromSearch,
  type CategoryNode,
  type ProductScope,
} from '../../api/browse.client';
import { SCOPE_LABEL_PARAM } from '../../api/landing.client';
import { BrowseMenu } from '../Browse/BrowseMenu';
import { CategoryListing } from '../Browse/CategoryListing';
import { useBrowseDrill } from '../Browse/useBrowseDrill';
import { ProductSearch } from '../Search/ProductSearch';
import { useHeaderChrome } from './useHeaderChrome';
import { SearchTypeahead } from './SearchTypeahead';
import { recordRecentSearch } from './recentSearches';
import { ScanModal } from '../Scan/ScanModal';
import { detectScanCapability } from '../Scan/scanCapability';
import { BROWSE_MENU_ID } from '../Browse/useBrowseMegaMenu';
import styles from './Products.module.css';

export interface ProductsProps {
  /** Epic 7 demo polish (2026-07-17) — `favourites` renders the SAME page shell
   *  with the listing in favourites view (prototype `?view=favourites`); any
   *  taxonomy navigation from there routes back to /products. */
  readonly view?: 'catalogue' | 'favourites';
}

export function Products({ view = 'catalogue' }: ProductsProps = {}) {
  const favouritesView = view === 'favourites';
  const navigate = useNavigate();
  const location = useLocation();

  // Story 8.1 (ADO-2010), Tasks 8.2-8.4 — the landing-tile scope rides in the URL
  // (`/products?hierarchyCode=…&cateringOnly=true`), so a tile link, a reload and Back
  // all land on the same scoped listing. `scopeLabel` is web-only (the chip's name) and
  // is never sent to the API. Memoised on the query string, so the listing's fetch
  // effect only re-runs when the scope really changes.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const scope = useMemo(() => productScopeFromSearch(new URLSearchParams(searchKey)), [searchKey]);
  const scopeLabel = new URLSearchParams(searchKey).get(SCOPE_LABEL_PARAM) ?? undefined;
  const changeScope = useCallback(
    (next: ProductScope) => {
      const params = new URLSearchParams();
      appendScopeParams(params, next);
      if (
        scopeLabel !== undefined &&
        (next.hierarchyCode !== undefined || next.globalFavourite !== undefined)
      ) {
        params.set(SCOPE_LABEL_PARAM, scopeLabel);
      }
      setSearchParams(params, { replace: true });
    },
    [scopeLabel, setSearchParams],
  );
  // A10.4: the Catering-only switch is offered to Foodservice accounts only.
  const offerCateringOnly = useSessionStore(
    (s) => s.activeTradingAccount?.tradingAccountType === 'FOODSERVICE',
  );

  // Search: the live box value + the COMMITTED term that drives a search, plus a
  // monotonic token so an identical re-submit still re-runs (page-mode parity).
  const [searchInput, setSearchInput] = useState('');
  const [committedTerm, setCommittedTerm] = useState('');
  const [searchNonce, setSearchNonce] = useState(0);

  // Favourites badge + depot switcher — the header chrome shared verbatim with
  // My Account / Admin Users (PR #148 review). The catalogue owns its own search
  // (inline, with typeahead) and Browse (the mega-menu below), so it composes the
  // base chrome hook rather than the catalogue-linking useCatalogueHeader.
  const {
    favouritesCount,
    depots,
    activeDepotId,
    onSelectDepot,
    depotName,
    depotCutOff,
    logo,
    themed,
    ...cart
  } = useHeaderChrome();

  // The active-basket lifecycle (open the depot's DRAFT basket, hydrate it, and
  // re-open on a depot switch) is mounted by useHeaderChrome above, like on every
  // other full-header screen.

  // Search typeahead (Epic 7 pixel parity, ADO-903): the dropdown opens on
  // focus/keystroke and closes on commit/clear (here) or Escape/click-outside
  // (inside SearchTypeahead). `typeaheadGroupId` is the dropdown's DOM id (the
  // suggestions are a labelled group of buttons, not an ARIA combobox listbox —
  // PR #139 review).
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const typeaheadGroupId = useId();

  // Browse mega-menu (Story 7.1 department selector) + the drill-state it drives.
  // No default Department (2026-07-17 demo feedback): the root state lists ALL
  // departments' products; drilling is an explicit filter the user applies.
  const drill = useBrowseDrill();
  const [browseOpen, setBrowseOpen] = useState(false);
  const drillNavigate = drill.navigate;

  // Camera barcode scan (Story 7.18, ADO-1387). The trigger is capability-gated
  // (AC1) — detected once per mount; the scan flow lives entirely in ScanModal, a
  // separate surface from the search box, so a scanned barcode NEVER lands in
  // `searchInput` (AC8 holds by construction — there is no code path here that writes
  // the barcode into the search state).
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCapability] = useState(detectScanCapability);
  // The scan modal's "Search by name" fallback (AC7/AC12) returns the user to text
  // search — focus the header input once the modal has closed and restored focus.
  const searchInputRef = useRef<HTMLInputElement>(null);

  // A handover carried in router state, applied once on mount then cleared so a
  // refresh doesn't re-apply it:
  //   • `node` — a category handed from the favourites view (rail / mega-menu).
  //   • `searchTerm` — a term submitted from another screen's SiteHeader search
  //     (My Account, Admin Users). Seed the box AND commit it so the catalogue
  //     actually runs the search; otherwise the term is silently dropped and the
  //     user lands on the full catalogue with an empty box (PR #148 review).
  useEffect(() => {
    if (favouritesView) return;
    const handed = location.state as { node?: CategoryNode; searchTerm?: string } | null;
    const handedNode = handed?.node;
    const handedTerm = handed?.searchTerm?.trim();
    if (handedNode === undefined && (handedTerm === undefined || handedTerm === '')) return;
    if (handedNode !== undefined) drillNavigate(handedNode);
    if (handedTerm !== undefined && handedTerm !== '') {
      setSearchInput(handedTerm);
      commitSearch(handedTerm);
    }
    // Keep the query string: it carries the landing-tile scope.
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    );
    // Mount-only: the handover is a one-shot route transition, not reactive state.
  }, []);

  // In favourites view, ANY taxonomy navigation leaves for the catalogue page
  // (`null` = the all-products root; a node rides along in router state).
  const goToCatalogue = (node: CategoryNode | null) => {
    navigate('/products', node === null ? undefined : { state: { node } });
  };
  const listingNavigate = favouritesView ? goToCatalogue : drill.navigate;

  // Manager-dashboard header link (Story 6.31, ADO-1253) — shown only to users who
  // can reach /admin/users (DISPLAY/ROUTING only; server re-checks, NFR18).
  const role = useSessionStore((s) => s.role);
  const catalogueEntryKey = useCatalogueEntryKey();

  // Commit `term` as the active search: the existing body swap, plus the
  // typeahead contract — record the committed term into the session recents and
  // close the dropdown. Every commit path funnels here (Enter, a suggestion
  // row's product name, a recent-term row, "View all").
  const commitSearch = (term: string) => {
    const trimmed = term.trim();
    setCommittedTerm(trimmed);
    if (trimmed !== '') recordRecentSearch(trimmed);
    setSearchNonce((n) => n + 1);
    setBrowseOpen(false);
    setTypeaheadOpen(false);
  };
  const submitSearch = () => commitSearch(searchInput);
  // A typeahead commit also places the committed term into the search box.
  const commitFromTypeahead = (term: string) => {
    setSearchInput(term);
    commitSearch(term);
  };
  const clearSearch = () => {
    setSearchInput('');
    setCommittedTerm('');
    setTypeaheadOpen(false);
  };

  // Escape and click-outside / hover-out close the mega-menu (the latter two
  // via onBrowseDismiss below); it also closes on toggling Browse again,
  // navigating home, committing a search, or picking a leaf node (see
  // onBrowseClick / clearSearch / BrowseMenu onSelectNode).
  useEffect(() => {
    if (!browseOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // Claim the Escape (preventDefault) so a window-level layer — the upsell
      // toast — does not also dismiss on the same key (PR #279 review).
      if (e.key === 'Escape') {
        e.preventDefault();
        setBrowseOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [browseOpen]);

  const searching = committedTerm !== '';

  // `--shoplink-header-offset` (the filters rail's sticky top/height input) is
  // now measured and written by SiteHeader itself: it owns the pin state, so it
  // can swap the offset between full-header and sub-nav-only heights live
  // (Epic 7 scroll-minimize — the old whole-header ResizeObserver here couldn't
  // see the pinned state).

  return (
    <div className={styles.page}>
      <SiteHeader
        searchValue={searchInput}
        onSearchChange={(value) => {
          setSearchInput(value);
          setTypeaheadOpen(true); // every keystroke (re)opens the dropdown
        }}
        onSearchSubmit={submitSearch}
        onSearchFocus={() => setTypeaheadOpen(true)}
        searchInputRef={searchInputRef}
        searchTypeahead={
          <SearchTypeahead
            query={searchInput}
            open={typeaheadOpen}
            listboxId={typeaheadGroupId}
            onClose={() => setTypeaheadOpen(false)}
            onCommit={commitFromTypeahead}
          />
        }
        onClearSearch={clearSearch}
        onNavigateHome={() => {
          clearSearch();
          setBrowseOpen(false);
        }}
        logo={logo}
        themed={themed}
        favouritesCount={favouritesCount}
        // Browse toggles the department mega-menu (and exits any active search).
        onBrowseClick={() => {
          clearSearch();
          setBrowseOpen((o) => !o);
        }}
        browseExpanded={browseOpen}
        browseMenuId={BROWSE_MENU_ID}
        // Clicking away from the menu, or letting the pointer leave the header
        // area, puts it away as well.
        onBrowseDismiss={() => setBrowseOpen(false)}
        depots={depots}
        activeDepotId={activeDepotId}
        onSelectDepot={onSelectDepot}
        depotName={depotName}
        depotCutOff={depotCutOff}
        showManagerDashboard={isAdminRole(role)}
        // Barcode scan (Story 7.18) — the catalogue is the surface that offers it;
        // the control is capability-gated (disabled-with-reason where unavailable).
        onScanClick={() => setScanOpen(true)}
        scanAvailable={scanCapability.available}
        scanUnavailableReason={scanCapability.reason}
        scanActive={scanOpen}
        {...cart}
      >
        {browseOpen && (
          <BrowseMenu
            id={BROWSE_MENU_ID}
            activeDepartmentId={drill.activeDepartment?.id ?? null}
            activeCategoryId={drill.activeCategory?.id ?? null}
            selectedNodeId={drill.selectedNode?.id ?? null}
            onClose={() => setBrowseOpen(false)}
            onSelectNode={(node) => {
              clearSearch();
              if (favouritesView) {
                goToCatalogue(node);
                setBrowseOpen(false);
                return;
              }
              drill.selectNode(node);
              // Close once a leaf (Sub-Category, or a childless node) is chosen.
              if (!node.hasChildren) setBrowseOpen(false);
            }}
            onExpandNode={drill.expandNode}
          />
        )}
      </SiteHeader>

      <main className={styles.main} aria-label="Products">
        {searching ? (
          // Full-bleed like the browse listing: the embedded search body now
          // carries the SAME re-skinned listing shell (pinned filters rail flush
          // to the viewport's left edge + content column with its own responsive
          // padding) — no extra wrapper padding here (Epic 7 pixel parity).
          // Keyed by the ordering entry (Enabler E7-C, ADO-2274, D-1): another entry is another
          // range, so the search starts afresh, re-runs the committed term and drops its filters.
          <ProductSearch
            key={catalogueEntryKey ?? 'no-entry'}
            embedded
            term={committedTerm}
            runToken={searchNonce}
          />
        ) : (
          <CategoryListing
            node={favouritesView ? null : drill.selectedNode}
            onNavigate={listingNavigate}
            favouritesView={favouritesView}
            scope={scope}
            scopeLabel={scopeLabel}
            onScopeChange={changeScope}
            offerCateringOnly={offerCateringOnly}
          />
        )}
      </main>

      {/* Camera barcode scan (Story 7.18) — self-contained flow; on a confirmed
          match it adds through the Story 7.9 one-click path (same cart/toast). It
          never writes to the search box, so the search field stays clean (AC8). */}
      <ScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        // "Search by name" fallback (AC7/AC12): the modal closes first (restoring focus
        // to the trigger), so defer the focus a frame to land it on the search input.
        onUseTextSearch={() => requestAnimationFrame(() => searchInputRef.current?.focus())}
      />
    </div>
  );
}

export default Products;
